import { useCallback, useState } from "react";
import { Button } from "@ndd/ui";
import type { ObservationNoteData } from "../../../../shared/observation-types";
import { ObservationRow } from "./observation-row";

interface ObservationsPanelProps {
  rdId: string;
  rdSlug: string;
  observations: ObservationNoteData[];
  /** Open the create form (band button is in the parent; empty state uses this). */
  onAdd: () => void;
  onEdit: (note: ObservationNoteData) => void;
  onDeleted: () => void;
  /**
   * Workspace surface mode: the list runs full height (the page scrolls)
   * instead of scrolling inside a fixed-height card viewport.
   */
  surface?: boolean;
}

export function ObservationsPanel({
  rdId,
  rdSlug,
  observations,
  onAdd,
  onEdit,
  onDeleted,
  surface = false,
}: ObservationsPanelProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const handleToggle = useCallback((noteId: string) => {
    setExpandedId((prev) => (prev === noteId ? null : noteId));
  }, []);

  if (observations.length === 0) {
    return (
      <div className="px-5 py-10 text-center text-muted-foreground">
        <div className="mb-1.5 font-sans text-title-sm font-semibold text-muted-strong">
          No observations yet
        </div>
        <p className="text-sm">Record a win, obstacle, or risk you&apos;ve noticed.</p>
        <div className="mt-4">
          <Button onClick={onAdd}>+ Add Observation</Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className={
          surface ? "border-t border-border bg-card" : "max-h-[360px] overflow-y-auto"
        }
      >
        {observations.map((note) => (
          <ObservationRow
            key={note.id}
            rdId={rdId}
            rdSlug={rdSlug}
            note={note}
            isExpanded={expandedId === note.id}
            onToggle={handleToggle}
            onEdit={onEdit}
            onDeleted={onDeleted}
          />
        ))}
      </div>
      {surface && (
        <p className="pb-2 pt-5 text-center text-xs text-subtle">
          {observations.length} of {observations.length} observation
          {observations.length === 1 ? "" : "s"}
        </p>
      )}
    </>
  );
}
